import { BarChart2, Info } from 'lucide-react';
import {
  buildTradeInsightAnalysis,
  type NarrativeSegment,
  type TradeInsightInput,
} from '../../lib/trades/tradeInsight';
import TooltipSimple from '../ui/TooltipSimple';

export type TradeInsightCardProps = TradeInsightInput & {
  className?: string;
};

const narrativeToneClasses: Record<
  NonNullable<NarrativeSegment['tone']>,
  string
> = {
  default: 'text-[#a1a1aa]',
  profit: 'text-emerald-400',
  loss: 'text-rose-400',
  neutral: 'text-[#d4d4d8]',
};

const renderNarrative = (segments: NarrativeSegment[]) =>
  segments.map((segment, index) => (
    <span
      key={`${segment.text}-${index}`}
      className={`${narrativeToneClasses[segment.tone ?? 'default']} ${
        segment.emphasis ? 'font-semibold tabular-nums' : ''
      }`}
    >
      {segment.text}
    </span>
  ));

export default function TradeInsightCard(props: TradeInsightCardProps) {
  const { className = '', ...analysisInput } = props;
  const analysis = buildTradeInsightAnalysis(analysisInput);
  const review = analysis.review;

  return (
    <section
      className={`group relative h-full w-full overflow-hidden rounded-xl border border-[#242428] bg-[#1a1a1c] text-[#f4f4f5] shadow-md ${className}`}
      aria-label="Trade Insight"
    >
      <div className="relative flex h-full flex-col justify-between px-6 pb-6 pt-6">
        <div>
          {/* Full-width card header */}
          <header className="-mx-6 -mt-6 mb-6 flex items-center justify-between gap-4 border-b border-[#242428] px-6 py-5">
            {/* Left side: icon, title and information tooltip */}
            <div className="flex min-w-0 items-center gap-2">
              <BarChart2
                className="h-4 w-4 shrink-0 text-rose-500/80"
                aria-hidden="true"
              />

              <h2 className="truncate text-[15px] font-semibold tracking-tight text-[#f4f4f5]">
                Trade Insight
              </h2>

              <TooltipSimple text="How the trade moved before closing.">
                <button
                  type="button"
                  aria-label="Trade Insight information"
                  className="shrink-0 rounded-sm text-[#52525b] outline-none transition-colors hover:text-[#a1a1aa] focus-visible:ring-2 focus-visible:ring-sky-500/70"
                >
                  <Info
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                </button>
              </TooltipSimple>
            </div>

            {/* Right side: trade metadata */}
            <div className="shrink-0 text-right text-[12px] font-medium text-[#a1a1aa]">
              {analysis.headerMetadata}
            </div>
          </header>

          {/* Trade narrative */}
          <div className="mb-6 border-l-2 border-rose-500/40 py-1 pl-4">
            <p
              className="text-[14px] leading-7 text-[#a1a1aa]"
              aria-label={analysis.narrative.accessibleText}
            >
              {renderNarrative(analysis.narrative.segments)}
            </p>
          </div>
        </div>

        {/* Trade review */}
        {review ? (
          <div
            data-testid="trade-insight-review"
            className="mt-4 flex flex-col gap-2.5"
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717a]">
              {review.title}
            </div>

            <div className="flex flex-col gap-2">
              {review.items.map((item, idx) => {
                const normalizedLabel = item.label.toLowerCase();

                const accentBorder =
                  normalizedLabel === 'positives'
                    ? 'border-l-2 border-emerald-500'
                    : normalizedLabel === 'primary issue'
                      ? 'border-l-2 border-rose-500'
                      : normalizedLabel === 'for review'
                        ? 'border-l-2 border-sky-500/70'
                        : 'border-l-2 border-sky-500/70';

                const labelColor =
                  normalizedLabel === 'positives'
                    ? 'text-emerald-400'
                    : normalizedLabel === 'primary issue'
                      ? 'text-rose-400'
                      : normalizedLabel === 'for review'
                        ? 'text-[#3B82F6]'
                        : 'text-[#a1a1aa]';

                const labelStyle = {
                  fontFamily: '"Inter Variable", Arial, sans-serif',
                } as const;

                const bodyStyle = {
                  fontFamily: '"Inter Variable", Arial, sans-serif',
                  fontWeight: 300,
                } as const;

                return (
                  <div
                    key={`${item.label}-${idx}`}
                    className={`rounded-lg border border-white/5 bg-[rgba(9,9,11,0.5)] px-3.5 py-2.5 ${accentBorder}`}
                  >
                    <div
                      className="text-[11px] font-bold uppercase tracking-[0.1em]"
                      style={labelStyle}
                    >
                      <span className={labelColor}>
                        {item.label}
                      </span>
                    </div>

                    <div className="mt-0.5 text-[13px] leading-snug text-[#f4f4f5]">
                      <span style={bodyStyle}>
                        {item.value}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
